/**
 * MapScene - Displays the map and allows player to choose their path
 */

import Phaser from 'phaser';
import { GameStateManager } from '../game/GameStateManager';
import { MapNode, NodeType, PieceType } from '../types';
import { SCENES, GAME_CONFIG, Z_DEPTHS } from '../constants/GameConstants';
import { T, INK, PALETTE, paintBackground, blend, fadeInScene } from '../constants/Theme';

export class MapScene extends Phaser.Scene {
  private gameStateManager!: GameStateManager;
  private nodeGraphics: Map<string, Phaser.GameObjects.Container> = new Map();
  private connectionGraphics: Phaser.GameObjects.Graphics[] = [];
  private playerKnight!: Phaser.GameObjects.Text;
  private cellSize: number = 60;
  private boardOffsetX: number = 0;
  private boardOffsetY: number = 0;
  private maxRow: number = 15;
  private rowPitch: number = 60; // vertical distance between display rows (compressed)
  private gridRows: number = 13;

  constructor() {
    super({ key: SCENES.MAP });
  }

  init(): void {
    this.gameStateManager = GameStateManager.getInstance();
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;

    // Backtick toggles debug menu
    this.input.keyboard?.on('keydown-BACKTICK', () => {
      if (this.scene.isActive(SCENES.DEBUG)) {
        this.scene.stop(SCENES.DEBUG);
      } else {
        this.scene.launch(SCENES.DEBUG);
      }
    });

    paintBackground(this, WIDTH, HEIGHT);
    fadeInScene(this);

    const title = this.add.text(WIDTH / 2, 38, 'CHOOSE YOUR PATH', T.heading());
    title.setOrigin(0.5);
    title.setLetterSpacing(2);
    title.setDepth(Z_DEPTHS.UI);

    // Calculate board positioning
    this.calculateBoardDimensions();

    // Draw chessboard background
    this.drawChessboard();

    // Draw the map
    this.drawMap();

    // Draw piece roster
    this.drawPieceRoster();

    // One-time first-run hint
    this.maybeShowMapHint();
  }

  private maybeShowMapHint(): void {
    let seen = false;
    try { seen = localStorage.getItem('chessrogue_map_hint_seen') === '1'; } catch { seen = false; }
    if (seen || this.gameStateManager.getCurrentNodeId()) return;
    try { localStorage.setItem('chessrogue_map_hint_seen', '1'); } catch { /* ignore */ }

    const hint = this.add.text(GAME_CONFIG.WIDTH / 2, 78,
      'Tap a glowing node to begin', T.body({ color: INK.gold, align: 'center' }));
    hint.setOrigin(0.5);
    hint.setDepth(Z_DEPTHS.UI + 5);
    this.tweens.add({ targets: hint, alpha: { from: 1, to: 0.55 }, duration: 700, yoyo: true, repeat: 4, ease: 'Sine.inOut',
      onComplete: () => this.tweens.add({ targets: hint, alpha: 0, duration: 400, onComplete: () => hint.destroy() }) });
  }

  private calculateBoardDimensions(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const mapData = this.gameStateManager.getMapData();

    if (!mapData) return;

    // Map rows are spaced 2 apart per level; compress them to one display row
    // each so the board can fill the screen width with large, square cells
    // instead of a thin tall column.
    const allNodes = Array.from(mapData.nodes.values());
    this.maxRow = Math.max(...allNodes.map(n => n.row));
    const levels = Math.ceil(this.maxRow / 2);     // distinct map ranks
    this.gridRows = levels + 2;                     // +1 padding top, +1 bottom

    const boardCols = 8;
    const titleSpace = 80;
    const rosterSpace = 120;
    const availableHeight = HEIGHT - titleSpace - rosterSpace;
    const availableWidth = WIDTH - 24;

    // Square cells sized to fill the width, capped so the whole map fits height
    const cellByWidth = availableWidth / boardCols;
    const cellByHeight = availableHeight / this.gridRows;
    this.cellSize = Math.min(cellByWidth, cellByHeight);
    this.rowPitch = this.cellSize; // square grid

    const boardPixelW = boardCols * this.cellSize;
    const boardPixelH = this.gridRows * this.rowPitch;
    this.boardOffsetX = (WIDTH - boardPixelW) / 2;
    this.boardOffsetY = titleSpace + (availableHeight - boardPixelH) / 2;
  }

  /** Compressed display row (0 = top/boss area) for a node's raw map row. */
  private displayRow(row: number): number {
    const levels = Math.ceil(this.maxRow / 2);
    return (levels - Math.round(row / 2)) + 1; // +1 = top padding row
  }

  private drawChessboard(): void {
    const mapData = this.gameStateManager.getMapData();
    if (!mapData) return;

    const boardWidth = 8;
    const boardHeight = this.gridRows;

    // Draw chessboard squares
    for (let row = 0; row < boardHeight; row++) {
      for (let col = 0; col < boardWidth; col++) {
        // Alternating colors (chess pattern)
        const isLightSquare = (row + col) % 2 === 0;
        const color = isLightSquare ? 0x2a3850 : 0x222f44; // dark slate chess pattern

        const x = this.boardOffsetX + col * this.cellSize;
        const y = this.boardOffsetY + row * this.rowPitch;

        const square = this.add.rectangle(x, y, this.cellSize, this.rowPitch, color);
        square.setOrigin(0);
        square.setDepth(-50); // Above background, behind everything else

        // Add subtle border
        const border = this.add.rectangle(
          x + this.cellSize / 2,
          y + this.rowPitch / 2,
          this.cellSize,
          this.rowPitch
        );
        border.setStrokeStyle(1, 0x000000, 0.15);
        border.setFillStyle(0x000000, 0);
        border.setDepth(-50);
      }
    }
  }

  private drawMap(): void {
    const mapData = this.gameStateManager.getMapData();
    if (!mapData) {
      console.error('[MapScene] No map data available');
      return;
    }

    console.log(`[MapScene] Drawing map with ${mapData.nodes.size} nodes`);

    const currentNodeId = this.gameStateManager.getCurrentNodeId();
    const currentNode = (currentNodeId ? mapData.nodes.get(currentNodeId) : null) ?? null;

    // At start of game, no current node - all starting nodes are available
    const startingNodeIds = new Set(mapData.startNodeIds);

    if (currentNode) {
      console.log(`[MapScene] Current node: ${currentNode.id} at (${currentNode.col}, ${currentNode.row})`);
    } else {
      console.log(`[MapScene] No current node - showing ${startingNodeIds.size} starting options`);
    }

    // Clear old graphics
    this.nodeGraphics.forEach((container) => container.destroy());
    this.nodeGraphics.clear();
    this.connectionGraphics.forEach(g => g.destroy());
    this.connectionGraphics = [];

    // Draw all connections first (so they appear behind nodes)
    mapData.nodes.forEach(node => {
      this.drawConnections(node, mapData, currentNode);
    });

    // Draw all nodes
    mapData.nodes.forEach(node => {
      this.drawNode(node, currentNode, startingNodeIds);
    });

    // Draw "see ahead 2" faded nodes if upgrade active
    if (this.gameStateManager.hasActiveUpgrade('see_ahead_2') && currentNode) {
      this.drawPeekAheadNodes(currentNode, mapData);
    }

    // Draw player knight on current node (only if we have one)
    if (currentNode) {
      this.drawPlayerKnight(currentNode);
    }

    // Draw map upgrade buttons
    this.drawMapUpgradeButtons(currentNode);

    console.log(`[MapScene] Finished drawing map`);
  }

  private drawPeekAheadNodes(currentNode: MapNode, mapData: any): void {
    // Show nodes 2 levels ahead with faded style
    const twoLevelsAhead = currentNode.level + 2;
    mapData.nodes.forEach((node: MapNode) => {
      if (node.level === twoLevelsAhead && !node.visited) {
        const pos = this.getNodePosition(node);
        const nodeSize = this.cellSize * 0.4;

        const circle = this.add.circle(pos.x, pos.y, nodeSize / 2, 0xaaaaaa, 0.3);
        circle.setStrokeStyle(2, 0x888888, 0.3);
        circle.setDepth(Z_DEPTHS.BOARD + 3);

        const icon = this.add.text(pos.x, pos.y, this.getNodeIcon(node.type), {
          fontSize: `${nodeSize * 0.5}px`,
          color: '#888888',
        });
        icon.setOrigin(0.5);
        icon.setAlpha(0.4);
        icon.setDepth(Z_DEPTHS.BOARD + 4);
      }
    });
  }

  private drawMapUpgradeButtons(currentNode: MapNode | null): void {
    const gsm = this.gameStateManager;
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const gameState = gsm.getGameState();
    const perActUsed = gameState.perActUsed || new Set<string>();
    const buttonY = HEIGHT - 30;
    const buttons: Array<{ label: string; key: string; action: () => void }> = [];

    // skip_battle_once: skip one battle node per act
    if (gsm.hasActiveUpgrade('skip_battle_once') && !perActUsed.has('skip_battle_once')) {
      buttons.push({
        label: '⏩ Skip Battle',
        key: 'skip_battle_once',
        action: () => {
          // Mark used
          if (!gameState.perActUsed) gameState.perActUsed = new Set();
          gameState.perActUsed.add('skip_battle_once');
        },
      });
    }

    // reroll_node_once: reroll one node per act
    if (gsm.hasActiveUpgrade('reroll_node_once') && !perActUsed.has('reroll_node_once')) {
      buttons.push({
        label: '🎲 Reroll Node',
        key: 'reroll_node_once',
        action: () => {
          if (!gameState.perActUsed) gameState.perActUsed = new Set();
          gameState.perActUsed.add('reroll_node_once');
          // Reroll a random connected node
          if (currentNode) {
            const mapData = gsm.getMapData();
            if (mapData) {
              const nodeTypes: NodeType[] = ['enemy', 'shop', 'heal', 'treasure', 'puzzle'];
              for (const connId of currentNode.connections) {
                const connNode = mapData.nodes.get(connId);
                if (connNode && !connNode.visited && connNode.type !== 'boss') {
                  connNode.type = nodeTypes[Math.floor(Math.random() * nodeTypes.length)];
                  break;
                }
              }
            }
          }
          this.drawMap();
        },
      });
    }

    if (buttons.length === 0) return;

    const buttonWidth = 130;
    const spacing = 10;
    const totalWidth = buttons.length * buttonWidth + (buttons.length - 1) * spacing;
    let startX = (WIDTH - totalWidth) / 2;

    for (const btn of buttons) {
      const text = this.add.text(startX, buttonY, btn.label, {
        fontSize: '13px',
        color: '#ffffff',
        backgroundColor: '#3498db',
        padding: { x: 8, y: 5 },
      });
      text.setDepth(Z_DEPTHS.UI);
      text.setInteractive({ useHandCursor: true });
      text.on('pointerdown', () => {
        btn.action();
        text.setBackgroundColor('#555555');
        text.disableInteractive();
      });
      text.on('pointerover', () => text.setBackgroundColor('#5dade2'));
      text.on('pointerout', () => text.setBackgroundColor('#3498db'));

      startX += buttonWidth + spacing;
    }
  }

  private drawNode(node: MapNode, currentNode: MapNode | null, startingNodeIds: Set<string>): void {
    const pos = this.getNodePosition(node);

    // Create container for node
    const container = this.add.container(pos.x, pos.y);

    // Determine if this node is available
    // - If no current node, only starting nodes are available
    // - Otherwise, nodes connected to current node are available
    const isStartingNode = startingNodeIds.has(node.id);
    // jump_to_any_node: all same-level nodes become clickable
    const gameState = this.gameStateManager.getGameState();
    const perActUsed = gameState.perActUsed || new Set<string>();
    const canJump = this.gameStateManager.hasActiveUpgrade('jump_to_any_node')
      && !perActUsed.has('jump_to_any_node')
      && currentNode
      && node.level === currentNode.level + 1
      && !node.visited;

    const isAvailable = currentNode
      ? (currentNode.connections.includes(node.id) || canJump)
      : isStartingNode;
    const isCurrent = currentNode ? node.id === currentNode.id : false;
    const isVisited = node.visited;

    // Node circle background
    const nodeSize = this.cellSize * 0.74;
    const accent = this.getNodeAccent(node.type);

    if (isAvailable) {
      // Soft glow ring behind available nodes
      const glow = this.add.circle(0, 0, nodeSize / 2 + 6, accent, 0.28);
      container.add(glow);
    }

    const circle = this.add.circle(0, 0, nodeSize / 2, this.getNodeColor());
    const borderColor = isAvailable ? accent : isVisited ? PALETTE.stroke : 0x2a3446;
    circle.setStrokeStyle(isAvailable ? 4 : 3, borderColor);
    container.add(circle);

    const icon = this.add.text(0, 0, this.getNodeIcon(node.type), {
      fontFamily: T.body().fontFamily,
      fontSize: `${nodeSize * 0.62}px`,
      fontStyle: '700',
      color: INK.dark,
    });
    icon.setOrigin(0.5);
    container.add(icon);

    // Make available nodes interactive
    if (isAvailable) {
      container.setSize(nodeSize, nodeSize);
      container.setInteractive({ useHandCursor: true });

      container.on('pointerover', () => {
        circle.setScale(1.1);
      });

      container.on('pointerout', () => {
        circle.setScale(1);
      });

      container.on('pointerdown', () => {
        this.selectNode(node);
      });
    }

    // Darken visited nodes
    if (isVisited && !isCurrent) {
      circle.setAlpha(0.5);
      icon.setAlpha(0.5);
    }

    container.setDepth(Z_DEPTHS.BOARD + 5); // Well above connections
    this.nodeGraphics.set(node.id, container);
  }

  private drawConnections(node: MapNode, mapData: any, currentNode: MapNode | null): void {
    const fromPos = this.getNodePosition(node);
    const isFromCurrentNode = currentNode ? node.id === currentNode.id : false;

    for (const connectionId of node.connections) {
      const toNode = mapData.nodes.get(connectionId);
      if (!toNode) continue;

      const toPos = this.getNodePosition(toNode);

      // Draw Bezier curve path
      this.drawBezierConnection(fromPos, toPos, node.visited, isFromCurrentNode);
    }
  }

  private drawBezierConnection(
    from: { x: number; y: number },
    to: { x: number; y: number },
    visited: boolean,
    isFromCurrentNode: boolean = false
  ): void {
    const graphics = this.add.graphics();

    // Gold for the path leaving your current node, muted slate otherwise
    const lineColor = isFromCurrentNode ? PALETTE.gold : blend(PALETTE.stroke, 0xffffff, 0.15);
    const lineAlpha = isFromCurrentNode ? 0.95 : (visited ? 0.35 : 0.6);

    graphics.lineStyle(isFromCurrentNode ? 5 : 3, lineColor, lineAlpha);

    // Calculate control point for smooth Bezier curve
    const dx = to.x - from.x;
    const dy = to.y - from.y;

    // Place control point to create a gentle arc
    // Use the midpoint with a perpendicular offset
    const midX = (from.x + to.x) / 2;
    const midY = (from.y + to.y) / 2;

    // Create perpendicular offset for smooth arc
    // The curve bows to the right relative to the direction of travel
    const distance = Math.sqrt(dx * dx + dy * dy);
    const curveAmount = Math.min(this.cellSize * 0.5, distance * 0.3);

    // Perpendicular vector (normalized)
    const perpX = -dy / distance;
    const perpY = dx / distance;

    const ctrlX = midX + perpX * curveAmount;
    const ctrlY = midY + perpY * curveAmount;

    // Draw dotted Bezier curve
    this.drawDottedBezier(graphics, from.x, from.y, ctrlX, ctrlY, to.x, to.y);

    graphics.setDepth(Z_DEPTHS.BOARD - 1); // Behind board squares and nodes
    this.connectionGraphics.push(graphics);
  }

  private drawDottedBezier(
    graphics: Phaser.GameObjects.Graphics,
    startX: number,
    startY: number,
    ctrlX: number,
    ctrlY: number,
    endX: number,
    endY: number
  ): void {
    const segments = 50; // Number of segments to approximate the curve
    const dashLength = 5;
    const gapLength = 5;

    // Sample points along the Bezier curve
    const points: { x: number; y: number }[] = [];
    for (let i = 0; i <= segments; i++) {
      const t = i / segments;
      const t1 = 1 - t;

      // Quadratic Bezier formula: B(t) = (1-t)²P0 + 2(1-t)tP1 + t²P2
      const x = t1 * t1 * startX + 2 * t1 * t * ctrlX + t * t * endX;
      const y = t1 * t1 * startY + 2 * t1 * t * ctrlY + t * t * endY;

      points.push({ x, y });
    }

    // Calculate cumulative distances along the curve
    const distances: number[] = [0];
    let totalLength = 0;
    for (let i = 1; i < points.length; i++) {
      const dx = points[i].x - points[i - 1].x;
      const dy = points[i].y - points[i - 1].y;
      totalLength += Math.sqrt(dx * dx + dy * dy);
      distances.push(totalLength);
    }

    // Draw dashed line along the curve
    let currentDist = 0;
    let drawing = true;

    for (let i = 1; i < points.length; i++) {
      const segmentStart = distances[i - 1];
      const segmentEnd = distances[i];

      if (drawing) {
        // Check if we should stop drawing
        if (currentDist + (segmentEnd - segmentStart) >= dashLength) {
          // Draw partial segment up to dashLength
          const remaining = dashLength - currentDist;
          const ratio = remaining / (segmentEnd - segmentStart);
          const stopX = points[i - 1].x + ratio * (points[i].x - points[i - 1].x);
          const stopY = points[i - 1].y + ratio * (points[i].y - points[i - 1].y);
          graphics.lineBetween(points[i - 1].x, points[i - 1].y, stopX, stopY);
          drawing = false;
          currentDist = 0;
        } else {
          // Draw full segment
          graphics.lineBetween(points[i - 1].x, points[i - 1].y, points[i].x, points[i].y);
          currentDist += segmentEnd - segmentStart;
        }
      } else {
        // In gap - check if we should start drawing again
        currentDist += segmentEnd - segmentStart;
        if (currentDist >= gapLength) {
          drawing = true;
          currentDist = 0;
        }
      }
    }
  }

  private drawPlayerKnight(node: MapNode): void {
    const pos = this.getNodePosition(node);

    // Draw knight piece (using chess knight symbol) on top of current node
    this.playerKnight = this.add.text(pos.x, pos.y, '♞', {
      fontSize: `${this.cellSize * 0.4}px`,
      color: '#000000',
    });
    this.playerKnight.setOrigin(0.5);
    this.playerKnight.setDepth(Z_DEPTHS.BOARD + 10); // On top of everything

    // Add subtle bounce animation
    this.tweens.add({
      targets: this.playerKnight,
      y: pos.y - 5,
      duration: 800,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
    });
  }

  private getNodePosition(node: MapNode): { x: number; y: number } {
    const x = this.boardOffsetX + node.col * this.cellSize + this.cellSize / 2;
    const y = this.boardOffsetY + this.displayRow(node.row) * this.rowPitch + this.rowPitch / 2;
    return { x, y };
  }

  private getNodeColor(): number {
    return 0xf4f7fb;
  }

  private getNodeAccent(type: NodeType): number {
    switch (type) {
      case 'enemy': return PALETTE.red;
      case 'elite': return 0xe67e22;
      case 'boss': return 0xc0392b;
      case 'shop': return PALETTE.gold;
      case 'treasure': return PALETTE.gold;
      case 'heal': return PALETTE.green;
      case 'puzzle': return PALETTE.blue;
      case 'start': return PALETTE.green;
      default: return PALETTE.stroke;
    }
  }

  private getNodeIcon(type: NodeType): string {
    switch (type) {
      case 'start':
        return '⚑';
      case 'enemy':
        return '⚔';
      case 'elite':
        return '✠';
      case 'shop':
        return '⬢';
      case 'heal':
        return '✚';
      case 'treasure':
        return '◆';
      case 'puzzle':
        return '?';
      case 'boss':
        return '♚';
      default:
        return '?';
    }
  }

  private drawPieceRoster(): void {
    const gameState = this.gameStateManager.getGameState();
    const currentPieces = gameState.playerPieces;
    const capturedPieces = gameState.capturedPieces;

    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const pieceSize = 40;
    const spacing = 50;

    // Collect all pieces (individually, including each pawn)
    const allPieces: { type: PieceType; isAlive: boolean }[] = [];

    // Standard chess order for display
    const pieceOrder: PieceType[] = ['k', 'q', 'r', 'b', 'n', 'p'];

    for (const pieceType of pieceOrder) {
      const alivePieces = currentPieces.filter(p => p.type === pieceType);
      const capturedCount = capturedPieces.filter(t => t === pieceType).length;

      // Add alive pieces
      for (let i = 0; i < alivePieces.length; i++) {
        allPieces.push({ type: pieceType, isAlive: true });
      }

      // Add captured pieces
      for (let i = 0; i < capturedCount; i++) {
        allPieces.push({ type: pieceType, isAlive: false });
      }
    }

    // Calculate total width and center position
    const totalWidth = allPieces.length * spacing;
    const startX = (WIDTH - totalWidth) / 2 + spacing / 2;
    const y = HEIGHT - 70;

    // Draw each piece
    allPieces.forEach((piece, index) => {
      const x = startX + index * spacing;

      if (piece.isAlive) {
        // Draw solid piece sprite
        const spriteKey = `w${piece.type}`;
        if (this.textures.exists(spriteKey)) {
          const sprite = this.add.sprite(x, y, spriteKey);
          sprite.setDisplaySize(pieceSize, pieceSize);
          sprite.setDepth(Z_DEPTHS.UI);
        }
      } else {
        // Draw captured piece with sprite and dotted outline
        const spriteKey = `w${piece.type}`;
        if (this.textures.exists(spriteKey)) {
          // Draw the piece sprite with reduced opacity
          const sprite = this.add.sprite(x, y, spriteKey);
          sprite.setDisplaySize(pieceSize, pieceSize);
          sprite.setAlpha(0.3);
          sprite.setTint(0x666666); // Gray tint
          sprite.setDepth(Z_DEPTHS.UI);

          // Draw dotted rectangle outline around the sprite
          const graphics = this.add.graphics();
          const dashLength = 4;
          const gapLength = 4;
          const halfSize = pieceSize / 2;

          graphics.lineStyle(2, 0x666666, 0.8);

          // Draw dashed rectangle by drawing dashed lines on each side
          const drawDashedLine = (x1: number, y1: number, x2: number, y2: number) => {
            const dx = x2 - x1;
            const dy = y2 - y1;
            const length = Math.sqrt(dx * dx + dy * dy);
            const segments = Math.floor(length / (dashLength + gapLength));
            const unitX = dx / length;
            const unitY = dy / length;

            for (let i = 0; i < segments; i++) {
              const startDist = i * (dashLength + gapLength);
              const endDist = startDist + dashLength;
              graphics.lineBetween(
                x1 + startDist * unitX,
                y1 + startDist * unitY,
                x1 + Math.min(endDist, length) * unitX,
                y1 + Math.min(endDist, length) * unitY
              );
            }
          };

          // Top, Right, Bottom, Left
          drawDashedLine(x - halfSize, y - halfSize, x + halfSize, y - halfSize);
          drawDashedLine(x + halfSize, y - halfSize, x + halfSize, y + halfSize);
          drawDashedLine(x + halfSize, y + halfSize, x - halfSize, y + halfSize);
          drawDashedLine(x - halfSize, y + halfSize, x - halfSize, y - halfSize);

          graphics.setDepth(Z_DEPTHS.UI + 1);
        }
      }
    });
  }

  private selectNode(node: MapNode): void {
    // Mark node as visited and set as current
    node.visited = true;
    this.gameStateManager.setCurrentNode(node.id);

    // Determine which scene to go to based on node type
    let nextScene = SCENES.BATTLE;

    switch (node.type) {
      case 'enemy':
        nextScene = SCENES.BATTLE;
        break;
      case 'elite':
      case 'boss':
        nextScene = SCENES.BOSS_INTRO;
        break;
      case 'puzzle':
        nextScene = SCENES.PUZZLE;
        break;
      case 'shop':
        nextScene = SCENES.SHOP;
        break;
      case 'heal':
        nextScene = SCENES.HEAL;
        break;
      case 'treasure':
        nextScene = SCENES.TREASURE;
        break;
    }

    // Transition to next scene
    this.scene.start(nextScene);
  }
}
